import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styleSearch from './HeadSearch.module.scss';
import searchResultsStyle from './SearchResults.module.scss';
import classNames from 'classnames';
import SearchResults from './SearchResults';
import {
  allAutocompleteThunk,
  allSuggestionsThunk,
  clearSuggestions,
  combinedSearchThunk,
  selectAutoCompleteTextPlus,
  selectSuggestions,
  loadSearchMetaData,
  clearEnhancedSearchAutoComplete,
  selectTopicQuery,
} from '../../../features/enhancedSearch';
import { saveRecentSearches } from '../../../features/enhancedSearch/recentSearchService';
import { useDebouncedCallback } from 'use-debounce';

function shouldWeShowSuggestionText(suggestedText, searchValue) {
  return (
    suggestedText !== '' &&
    searchValue.length > 3 &&
    `${suggestedText}`.startsWith(searchValue) &&
    suggestedText !== searchValue
  );
}

export const HeadSearch = ({
  classicView: menuBarView,
  className,
  push,
  isDesktop,
  isAppMode,
  searchQuery,
}) => {
  const dropdownMobileRef = useRef(null);
  const inputFieldRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const prevSearchValueRef = useRef('');
  const dispatch = useDispatch();
  const suggestionsSelector = useSelector(selectSuggestions);
  const suggestedText = useSelector(selectAutoCompleteTextPlus);
  const previousSearchQuery = useSelector(selectTopicQuery);
  const [showSavedOrRecent, setshowSavedOrRecent] = useState(false);
  const [showSuggestion, setShowSuggestion] = useState(false);

  const showSuggestedText = shouldWeShowSuggestionText(
    suggestedText,
    searchValue
  );
  const fetchAucompleteAndSuggestion = useDebouncedCallback(
    (searchTerm, isAutoComplete) => {
      const trimmedValue = searchTerm.trim().toLowerCase();
      if (trimmedValue && trimmedValue !== '') {
        dispatch(allSuggestionsThunk({ searchText: trimmedValue }));
        if (!isAutoComplete) {
          dispatch(allAutocompleteThunk({ searchText: trimmedValue }));
        }
        setIsOpen(true);
        setshowSavedOrRecent(false);
        setShowSuggestion(true);
      }
    },
    800
  );

  const handleInputChange = (e) => {
    setIsOpen(true);
    const newValue = e.target.value;
    setSearchValue(newValue);

    if (newValue === "") {
      setShowSuggestion(false);
      setshowSavedOrRecent(true);
      return;
    }

    if (newValue !== prevSearchValueRef.current) {
      setShowSuggestion(false);
      setshowSavedOrRecent(false);

      if (newValue.length >= 3) {
        fetchAucompleteAndSuggestion(newValue, false);
      }
    }
  };

  const handleFocus = () => {
    if (isAppMode) {
      push('/app/searchLanding');
    }
    setIsOpen(true);
    setshowSavedOrRecent(true);

    if (searchValue === prevSearchValueRef.current && searchValue.trim()) {
      setshowSavedOrRecent(false);
    }
  };

  const handleBlur = () => {
    prevSearchValueRef.current = searchValue;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && searchValue.length >= 3) {
      fetchAucompleteAndSuggestion.cancel();
      dispatch(clearEnhancedSearchAutoComplete());
      dispatch(clearSuggestions());
      dispatch(
        combinedSearchThunk({
          searchText: searchValue,
          top: previousSearchQuery?.top,
        })
      );
      push(
        `/searchAction/?appendedinputbuttons=${encodeURIComponent(searchValue)}`
      );
      inputFieldRef.current.blur();
      setIsOpen(false);
    }
    if ((e.key === 'Tab' || e.keyCode === 9) && searchValue.length >= 3) {
      if (suggestedText && suggestedText !== searchValue) {
        // When displayed on the homescreen, the tab moves the cursor to a cancel search button
        // which isn't desired.
        if (!menuBarView) {
          e.preventDefault();
        }
        setSearchValue(suggestedText);
        fetchAucompleteAndSuggestion(suggestedText, true);
        setIsOpen(true);
      }
    } else {
      fetchAucompleteAndSuggestion.cancel();
    }
  };

  useEffect(() => {
    dispatch(loadSearchMetaData());
  }, [dispatch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownMobileRef.current &&
        !dropdownMobileRef.current.contains(event.target) &&
        event.target !== inputFieldRef.current
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(searchQuery);
    if (searchParams.has('appendedinputbuttons')) {
      const initialValue = searchParams.get('appendedinputbuttons');
      setSearchValue(initialValue);
      prevSearchValueRef.current = initialValue;
    }
  }, [searchQuery]);

  const clearSearch = () => {
    setSearchValue('');
    setIsOpen(false);
    setshowSavedOrRecent(false);
    setShowSuggestion(false);
    inputFieldRef.current.blur();
  };

  const suggestionItemClick = (item) => {
    try {
      saveRecentSearches('combinedSearch', {
        searchText: item.suggestion,
        url: item.url,
      });
      push(item.url);
      inputFieldRef.current.blur();
    } catch (error) {
      console.error(error);
    }
    setIsOpen(false);
  };

  const keypressSearchHandler = (event) => {
    if (event.keyCode === 13 && searchValue?.length >= 3) {
      fetchAucompleteAndSuggestion.cancel();
      setIsOpen(false);
      dispatch(
        combinedSearchThunk({
          searchText: searchValue,
          top: previousSearchQuery?.top,
        })
      );
      push(
        '/searchAction/?appendedinputbuttons=' + encodeURIComponent(searchValue)
      );
      inputFieldRef.current.blur();
    }
  };

  return (
    <div
      className={classNames({
        [styleSearch["classic-container"]]: menuBarView,
        [styleSearch["container"]]: menuBarView !== true,
        [styleSearch["relative"]]: isDesktop === true,
        [styleSearch["appMode"]]: isAppMode === true,
        "size-16": isAppMode === true,
        "size-14": isAppMode !== true,
        [styleSearch[className]]: styleSearch[className] !== undefined,
      })}
    >
      {isAppMode && (
        <img
          src="/TherapeuticGuidelines_Original_R_Logo.svg"
          alt="Therapeutic Guidelines"
        />
      )}
      <div
        className={classNames(
          `tg-icons-Magnifier ${styleSearch["icon-placement"]}`,
          {
            [styleSearch["classic-icon"]]: menuBarView,
          },
        )}
      >
        {showSuggestedText && (
          <>
            <span
              className={classNames(
                styleSearch["suggestedText"],
                { [styleSearch["classicSuggestion"]]: menuBarView },
                "medium",
              )}
            >
              {suggestedText}
            </span>
          </>
        )}

        <input
          type="search"
          name="appendedinputbuttons"
          id="searchBoxEnhanced"
          className={classNames(styleSearch.inputsearch, {
            [styleSearch["classic"]]: menuBarView,
            [styleSearch["inputAppMode"]]: isAppMode === true,
          })}
          placeholder="Search"
          autoComplete="off"
          onKeyDown={handleKeyDown}
          value={searchValue}
          onChange={handleInputChange}
          ref={inputFieldRef}
          aria-autocomplete="both"
          aria-owns="searchResults"
          data-testid="search-input"
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        {menuBarView !== true && showSuggestedText && (
          <span className={classNames(styleSearch["showText"], "medium")}>
            {searchValue}
          </span>
        )}
        {menuBarView === true && (
          <button
            type="button"
            className={styleSearch["search-button"]}
            data-testid="magnifier"
            onClick={() => keypressSearchHandler({ keyCode: 13 })}
          >
            <i className="flaticon-magnifying-glass34"></i>
          </button>
        )}
        {menuBarView !== true && searchValue !== "" && (
          <button
            onClick={clearSearch}
            aria-label="Clear Search"
            data-testid="clear-button"
            type="button"
            className={classNames(styleSearch["clear-button"], "size-24")}
          >
            <i className="tg-icons-Xmark"></i>
          </button>
        )}
      </div>
      {isOpen && (
        <SearchResults
          ref={dropdownMobileRef}
          items={suggestionsSelector}
          handleSelect={suggestionItemClick}
          type="search"
          resultStyle={searchResultsStyle}
          showSavedOrRecent={showSavedOrRecent}
          showSuggestion={showSuggestion}
        />
      )}
    </div>
  );
};
